import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatDate, formatReadCount } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  layout?: 'horizontal' | 'vertical';
}

export default function ArticleCard({ article, layout = 'horizontal' }: ArticleCardProps) {
  const isVariety = article.category === 'variety';

  if (layout === 'vertical') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer h-full"
      >
        <div className="w-full h-44 overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 搞鸡娱乐资讯网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-2.5">
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full ${
                isVariety ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
              }`}
            >
              {isVariety ? '综艺资讯' : '娱乐新闻'}
            </span>
            <span className="text-[11px] text-foreground-500">{article.platform}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2 flex-1">
            {article.summary}
          </p>
          <div className="flex items-center justify-between mt-3.5 text-xs text-foreground-500">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatReadCount(article.readCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex gap-4 md:gap-5 bg-background-50 rounded-lg p-3 md:p-4 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-32 h-24 md:w-44 md:h-28 rounded-md overflow-hidden shrink-0">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 搞鸡娱乐资讯网`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0 py-0.5">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full ${
              isVariety ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
            }`}
          >
            {isVariety ? '综艺资讯' : '娱乐新闻'}
          </span>
          <span className="text-[11px] text-foreground-500">{article.platform}</span>
        </div>
        <h3 className="font-heading text-sm md:text-lg font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
          {article.title}
        </h3>
        <p className="hidden sm:block text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2 flex-1">
          {article.summary}
        </p>
        <div className="flex items-center gap-4 mt-2 text-xs text-foreground-500">
          <span>{formatDate(article.publishedAt)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" /> {formatReadCount(article.readCount)}
          </span>
          <span className="hidden sm:flex items-center gap-1">
            <i className="ri-price-tag-3-line" /> {article.tags[0]}
          </span>
        </div>
      </div>
    </Link>
  );
}